import React from 'react';
import { Listbox } from '@headlessui/react';

import { toggleItem } from 'helpers/utils';
import { Label, Popup } from 'ui';

import SelectOption from './SelectOption';

type Props<T> = {
  id?: string;
  label?: string;
  choices?: { value: T; label: string }[];
  value: T[] | undefined;
  placeholder?: string;
  helpText?: string;
  error?: string;
  minWidth?: number;
  onChange: (newValue: T[]) => void;
  checkPosition?: 'left' | 'right';
  position?: React.ComponentProps<typeof Popup>['position'];
};

// todo tailwind disabled, readonly

function Multiselect<T>({
  id,
  label,
  choices,
  value,
  placeholder = gettext('Select...'),
  helpText,
  error,
  minWidth = undefined,
  onChange,
  checkPosition = 'right',
  position = 'bottom left',
}: Props<T>) {
  const toggleValue = (itemValue: T) => onChange(toggleItem(value, itemValue));

  const handleLabelRemoveClick = (itemValue: T) => (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onChange(value.filter((item) => item !== itemValue));
  };

  return (
    <Listbox as="div" id={id} value={value} onChange={toggleValue}>
      {({ open }) => (
        <>
          {label && (
            <Listbox.Label className="block text-sm font-medium text-gray-700" style={{ minWidth }}>
              {label}
            </Listbox.Label>
          )}

          <div className="relative mt-1">
            <Popup
              on="click"
              fluid
              className="w-full rounded-md bg-white shadow-lg"
              position={position}
              open={open}
              trigger={
                <Listbox.Button className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm">
                  <span className="-mt-1 block">
                    {value &&
                      value.map((itemValue) => (
                        <span className="mt-1 mr-1 inline-block">
                          <Label onRemove={handleLabelRemoveClick(itemValue)}>
                            {choices?.find((item) => item.value === itemValue)?.label}
                          </Label>
                        </span>
                      ))}
                    {(!value || value.length === 0) && (
                      <span className="mt-1 inline-block text-gray-300">{placeholder}</span>
                    )}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                </Listbox.Button>
              }
            >
              <Listbox.Options className="max-h-60 w-full overflow-auto rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {choices?.map((item) => (
                  <Listbox.Option value={item.value} key={item.label}>
                    {({ selected, active }) => (
                      <SelectOption
                        selected={selected}
                        active={active}
                        checkPosition={checkPosition}
                        label={item.label}
                      />
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Popup>

            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            {helpText && !error && <p className="mt-2 text-sm text-gray-500">{helpText}</p>}
          </div>
        </>
      )}
    </Listbox>
  );
}

export default Multiselect;
